import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Shield,
  FileText,
  Clock,
  BookOpen,
  Heart,
  Users,
  MapPin,
  Mail,
  Phone,
  Check,
  ArrowRight,
  Church,
  GraduationCap,
  Sprout,
  HeartHandshake,
  ShieldCheck,
  Sparkles,
  Lock,
  Loader2,
} from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { api } from "@/lib/api";

const HERO_IMG =
  "https://images.pexels.com/photos/34618500/pexels-photo-34618500.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const STUDY_IMG =
  "https://images.pexels.com/photos/6248420/pexels-photo-6248420.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const BIBLE_IMG =
  "https://images.pexels.com/photos/11937373/pexels-photo-11937373.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

function useSettings() {
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => setSettings({}));
  }, []);
  return settings;
}

export default function HomePage() {
  const settings = useSettings();
  const fee = settings?.enrollment_fee_per_child ?? 1500;
  const ministryNote =
    settings?.partner_ministry_note ??
    "A partner ministry is assisting with tuition costs for this first school year.";
  const contactEmail = settings?.contact_email || "zca.k.12.26@outlook.com";
  const contactPhone = settings?.contact_phone || "(205) 200-6257";
  const phoneHref = "tel:" + contactPhone.replace(/[^\d+]/g, "");
  const contactAddress = [settings?.contact_address_line1, settings?.contact_address_line2]
    .filter(Boolean)
    .join(", ") || "20670 Jimmy Lunceford Rd, Northport, AL 35475";
  const faqItems = (Array.isArray(settings?.faq_items) && settings.faq_items.length > 0) ? settings.faq_items : FAQ;

  // While settings load, show a minimal shell so we don't flash the full site
  // before we know whether enrollment is open.
  if (settings === null) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <SiteHeader />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="animate-spin text-purple" size={30} />
        </div>
        <SiteFooter />
      </div>
    );
  }

  // Coming-Soon gate — same toggle that closes /enroll also closes the site.
  if (settings.enrollment_open === false) {
    return (
      <div className="min-h-screen bg-white flex flex-col" data-testid="coming-soon-page">
        <SiteHeader />
        <section className="relative flex-1 flex items-center justify-center overflow-hidden bg-purple-dark">
          {/* Subtle decorative accents — no photo, so text stays readable */}
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg,#f0d875 0,#f0d875 1px,transparent 1px,transparent 22px)",
            }}
            aria-hidden
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at center, transparent 0%, rgba(30,10,40,0.55) 100%)",
            }}
            aria-hidden
          />
          <div className="relative max-w-3xl w-full mx-auto px-5 sm:px-8 py-20 sm:py-28 text-center">
            <img src="/zca-logo.png" alt="Zion Christian Academy" className="mx-auto h-20 w-20 bg-white rounded-full p-2 mb-8" />
            <div className="inline-flex items-center gap-2 border border-gold/60 text-gold-light text-[11px] tracking-[0.22em] uppercase font-bold px-4 py-1.5 mb-10">
              <Lock size={12} /> {settings.coming_soon_pill_text || "Enrollment opens soon"}
            </div>
            <h1
              className="h-serif text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-6"
              data-testid="coming-soon-headline"
              dangerouslySetInnerHTML={{
                __html: settings.coming_soon_headline_html ||
                  'A <span class="text-gold-light italic">Christ-centered</span> school<br class="hidden sm:block" /> is coming to Berry, Alabama.',
              }}
            />
            <div className="h-px w-24 bg-gold/60 mx-auto mb-8" />
            <p className="text-white/90 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-12" data-testid="coming-soon-body">
              {settings.coming_soon_body ||
                "Zion Christian Academy is preparing to welcome K–12 families for the 2026–2027 school year. Enrollment is temporarily paused while we finish setting up. We'll announce when applications reopen — reach out any time in the meantime."}
            </p>
            <div className="grid sm:grid-cols-2 gap-3 max-w-md mx-auto mb-12">
              <a
                href={`mailto:${contactEmail}`}
                className="bg-gold text-ink hover:bg-gold-light font-bold px-5 py-3 rounded-sm text-sm inline-flex items-center justify-center gap-2"
                data-testid="coming-soon-email"
              >
                <Mail size={14} /> {contactEmail}
              </a>
              <a
                href={phoneHref}
                className="border border-gold-light/60 text-gold-light hover:bg-white/5 font-bold px-5 py-3 rounded-sm text-sm inline-flex items-center justify-center gap-2"
                data-testid="coming-soon-phone"
              >
                <Phone size={14} /> {contactPhone}
              </a>
            </div>
            <div className="text-xs text-white/70 leading-relaxed border-t border-white/10 pt-6 mt-8">
              {settings.coming_soon_location_line1 || "Meeting at Alta Independent Baptist Church"}<br />
              {settings.coming_soon_location_line2 || "131 Willie Williams Rd · Berry, AL 35546"}
            </div>
          </div>
        </section>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div id="home" className="min-h-screen bg-white">
      <SiteHeader />

      {/* ---------- HERO ---------- */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${HERO_IMG}')` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-purple-dark/85 hero-noise" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32 lg:py-40">
          <div className="max-w-3xl animate-fade-up">
            <div className="inline-flex items-center gap-2 border border-gold/50 text-gold-light text-[11px] tracking-[0.22em] uppercase font-bold px-3 py-1.5 mb-8">
              <Church size={12} />
              <span data-testid="hero-pill">{settings.hero_pill_text || "Independent Christian Community School · Est. 2026"}</span>
            </div>
            <h1
              data-testid="hero-heading"
              className="h-serif text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-6"
              dangerouslySetInnerHTML={{
                __html: settings.hero_headline_html ||
                  'Built on <span class="text-gold-light italic"> Scripture, </span> through Christ, and in love.',
              }}
            />
            <p className="text-white/85 text-base sm:text-lg leading-relaxed max-w-2xl mb-10" data-testid="hero-subheadline">
              {settings.hero_subheadline ||
                "It is with great joy that we introduce Zion Christian Academy. Our desire is to partner with families to cultivate students who love the Lord, think biblically, and serve faithfully in their homes, churches, and communities."}
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Link
                to="/enroll"
                data-testid="hero-enroll-cta"
                className="group inline-flex items-center gap-2 bg-gold text-ink hover:bg-gold-light font-bold px-8 py-4 rounded-sm transition-colors"
              >
                Start Enrollment
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href="#how-it-works"
                className="inline-flex items-center gap-2 text-white hover:text-gold-light transition-colors px-4 py-3 text-sm border-b border-transparent hover:border-gold-light"
                data-testid="hero-learn-more"
              >
                Learn how it works
              </a>
            </div>

            {/* Trust badges */}
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl border-t border-white/15 pt-8">
              <TrustBadge Icon={Shield} label="Alabama-law compliant" />
              <TrustBadge Icon={FileText} label="Personal admin review" />
              <TrustBadge Icon={Clock} label="2–3 minutes to apply" />
            </div>
          </div>
        </div>
      </section>

      {/* ---------- ABOUT THE SCHOOL ---------- */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid gap-12 lg:gap-16 lg:grid-cols-12 items-center">
          <div className="lg:col-span-5">
            <div className="relative">
              <img
                src={STUDY_IMG}
                alt="Children studying at home"
                className="w-full h-[420px] object-cover border border-grey"
              />
              <div className="absolute -bottom-6 -right-6 hidden md:block bg-gold text-ink px-6 py-4 max-w-[220px]">
                <div className="h-serif text-2xl leading-tight">K–12</div>
                <div className="text-xs uppercase tracking-widest font-bold">
                  All grades welcome
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="text-xs tracking-[0.22em] uppercase text-purple-light font-bold mb-4">
              About the school
            </div>
            <h2 className="h-serif text-purple-dark text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-6">
              In-person Christian education, K–12.
            </h2>
            <p className="text-ink/90 text-base leading-relaxed mb-4">
              Zion Christian Academy is a full-time Christian day school
              operating under Alabama's church school statute (Ala. Code
              § 16-28-1). Classes meet in person at the fellowship hall of
              Alta Independent Baptist Church in Berry, Alabama.
            </p>
            <p className="text-ink/90 text-base leading-relaxed mb-8">
              Our director, Matthew Brady Renfroe, and our primary teacher,
              Shannon Renfroe, lead every student through a biblically-grounded
              curriculum, weekly chapel, and the daily rhythms of a Christ-centered
              classroom.
            </p>
            <ul className="grid sm:grid-cols-2 gap-3">
              {[
                "Monday–Thursday, 8am–2pm",
                "Chapel every Wednesday at 1pm",
                "K–12 grades offered",
                "Meeting at Alta IBC fellowship hall",
                "Bring your own lunch",
                "Open to all Christian families",
              ].map((line) => (
                <li key={line} className="flex items-start gap-2 text-sm text-ink">
                  <Check size={16} className="text-gold mt-0.5 shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ---------- VISION & MISSION ---------- */}
      <section id="vision-mission" className="py-24 sm:py-32 bg-white border-t border-grey">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-16">
            <div className="text-xs tracking-[0.22em] uppercase text-purple-light font-bold mb-4">
              Vision &amp; Mission
            </div>
            <h2 className="h-serif text-purple-dark text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Rooted in Scripture. Bold for Christ.
            </h2>
          </div>

          <div className="grid gap-14 md:gap-20 md:grid-cols-2">
            <VerseBlock
              overline="Our Vision"
              verse={"That the generation to come might know them, even the children which should be born; who should arise and declare them to their children."}
              reference="Psalm 78:6 · KJV"
              statement="Zion Christian Academy envisions a generation firmly rooted in the Word of God, equipped to stand boldly for Christ, and prepared to influence the world through unwavering faith and righteous living."
            />
            <VerseBlock
              overline="Our Mission"
              verse={"Train up a child in the way he should go: and when he is old, he will not depart from it."}
              reference="Proverbs 22:6 · KJV"
              statement="To provide a biblically-grounded education that nurtures the heart, strengthens the mind, and shapes the character of every student — integrating Scripture into all areas of learning."
            />
          </div>
        </div>
      </section>

      {/* ---------- HOW IT WORKS (4 STEPS) ---------- */}
      <section id="how-it-works" className="py-24 sm:py-32 bg-grey-light">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl mb-16">
            <div className="text-xs tracking-[0.22em] uppercase text-purple-light font-bold mb-4">
              How enrollment works
            </div>
            <h2 className="h-serif text-purple-dark text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-4">
              Four steps. Personally reviewed.
            </h2>
            <p className="text-ink/80 leading-relaxed">
              Every application is personally reviewed by our director — we want
              to know your family. Once approved, your official documents are
              released by email.
            </p>
          </div>

          <div className="grid md:grid-cols-4 border border-grey bg-white">
            <Step
              n="01"
              title="Apply online"
              body="Fill in parent & student details, choose your payment option, and sign the church school agreement. Takes about 2–3 minutes."
            />
            <Step
              n="02"
              title="Admin review"
              body="Our director will personally review your application and reach out to welcome your family — usually within 1–2 business days."
              divider
            />
            <Step
              n="03"
              title="Submit payment"
              body="Once approved, pay by cash or check — or arrange a payment plan for the end of the school year."
              divider
            />
            <Step
              n="04"
              title="Receive your documents"
              body="Enrollment form, welcome letter, and BOE instructions delivered to your email and parent portal."
              divider
            />
          </div>
        </div>
      </section>

      {/* ---------- TUITION ---------- */}
      <section id="tuition" className="py-24 sm:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <div className="text-xs tracking-[0.22em] uppercase text-purple-light font-bold mb-4">
            Tuition &amp; payment
          </div>
          <h2 className="h-serif text-purple-dark text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-4" data-testid="tuition-headline">
            {settings.tuition_headline || "Simple, transparent tuition."}
          </h2>
          <p className="text-ink/80 leading-relaxed max-w-2xl mx-auto mb-10" data-testid="tuition-subheadline">
            {settings.tuition_subheadline || "One flat rate per child for the entire school year. No hidden fees. No card processing — pay by cash or check."}
          </p>

          <div className="border border-gold bg-white px-8 sm:px-12 py-12 max-w-2xl mx-auto">
            <div className="text-xs tracking-[0.22em] uppercase text-gold font-bold mb-3">
              First-year tuition
            </div>
            <div className="h-serif text-6xl sm:text-7xl text-purple-dark tracking-tight mb-2">
              ${fee.toLocaleString()}
            </div>
            <div className="text-sm text-ink/70 mb-6">per child, per school year</div>
            <div className="h-px w-16 bg-gold mx-auto mb-6" />
            <p className="h-serif italic text-purple text-base leading-relaxed">
              {ministryNote}
            </p>

            <div className="mt-10 grid sm:grid-cols-2 gap-6 text-left">
              <div className="border border-grey p-5">
                <div className="text-xs tracking-[0.2em] uppercase text-purple-light font-bold mb-2">
                  Option 1
                </div>
                <div className="h-serif text-lg text-purple-dark mb-2" data-testid="tuition-option1-label">
                  {settings.tuition_option1_label || "Cash or check"}
                </div>
                <div className="text-sm text-ink/80 leading-relaxed" data-testid="tuition-option1-body">
                  {settings.tuition_option1_body || "Pay by cash or check at time of enrollment. Instructions emailed to you."}
                </div>
              </div>
              <div className="border border-grey p-5">
                <div className="text-xs tracking-[0.2em] uppercase text-purple-light font-bold mb-2">
                  Option 2
                </div>
                <div className="h-serif text-lg text-purple-dark mb-2" data-testid="tuition-option2-label">
                  {settings.tuition_option2_label || "Payment plan"}
                </div>
                <div className="text-sm text-ink/80 leading-relaxed" data-testid="tuition-option2-body">
                  {settings.tuition_option2_body || "Need to pay at the end of the year? Contact us to arrange a schedule that fits your family."}
                </div>
              </div>
            </div>

            <Link
              to="/enroll"
              data-testid="tuition-enroll-cta"
              className="mt-10 inline-flex items-center gap-2 bg-purple text-white hover:bg-purple-dark font-bold px-8 py-4 rounded-sm transition-colors"
            >
              Start Enrollment
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* ---------- VALUES BAND (6 Core Values) ---------- */}
      <section id="values" className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${BIBLE_IMG}')` }}
          aria-hidden
        />
        <div className="absolute inset-0 bg-purple-dark/90" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
          <div className="max-w-2xl mb-14">
            <div className="text-xs tracking-[0.22em] uppercase text-gold-light font-bold mb-4">
              Our core values
            </div>
            <h2 className="h-serif text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Six pillars of a Christ-centered classroom.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-x-10 gap-y-14">
            <Value
              Icon={BookOpen}
              title="Biblical Authority"
              body="We uphold the King James Version of the Holy Scriptures as our final authority for faith and practice. Every subject, policy, and decision is shaped by God's Word."
            />
            <Value
              Icon={Heart}
              title="Christlike Character"
              body="We cultivate humility, integrity, compassion, diligence, and holiness in every student — following the example of our Lord Jesus Christ."
            />
            <Value
              Icon={GraduationCap}
              title="Academic Excellence"
              body="We pursue rigorous, high-quality education that challenges students to think critically, communicate effectively, and steward their God-given abilities."
            />
            <Value
              Icon={Sprout}
              title="Discipleship & Spiritual Growth"
              body="We prioritize daily spiritual formation through prayer, Scripture, worship, and mentorship — encouraging students to grow in their walk with Christ."
            />
            <Value
              Icon={Users}
              title="Family Partnership"
              body="We believe parents are the primary educators of their children. ZCA exists to support, strengthen, and partner with families in their God-given calling."
            />
            <Value
              Icon={HeartHandshake}
              title="Service & Leadership"
              body="We train students to serve others with humility and to lead with courage, following Christ's command to be 'the light of the world.' (Matthew 5:14)"
            />
          </div>
        </div>
      </section>

      {/* ---------- FAQ ---------- */}
      <section id="faq" className="py-24 sm:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <div className="text-xs tracking-[0.22em] uppercase text-purple-light font-bold mb-4">
            Frequently asked questions
          </div>
          <h2 className="h-serif text-purple-dark text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-10">
            Answers before you enroll.
          </h2>

          <Accordion type="single" collapsible className="border-t border-grey" data-testid="faq-accordion">
            {faqItems.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="border-b border-grey"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="h-serif text-left text-purple-dark text-lg py-5 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-ink/85 text-base leading-relaxed pb-6 whitespace-pre-line">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ---------- CONTACT ---------- */}
      <section id="contact" className="py-24 sm:py-32 bg-grey-light">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid gap-12 lg:grid-cols-2 items-start">
          <div>
            <div className="text-xs tracking-[0.22em] uppercase text-purple-light font-bold mb-4">
              Get in touch
            </div>
            <h2 className="h-serif text-purple-dark text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-6">
              Questions? Talk with our director.
            </h2>
            <p className="text-ink/85 leading-relaxed mb-8 max-w-lg">
              We'd be honored to walk with your family through this decision. Reach
              out any time — evenings and weekends are welcome.
            </p>
            <div className="space-y-4">
              <ContactRow Icon={Mail} label="Email" value={contactEmail} href={`mailto:${contactEmail}`} />
              <ContactRow Icon={Phone} label="Phone" value={contactPhone} href={phoneHref} />
              <ContactRow
                Icon={MapPin}
                label="Address"
                value={contactAddress}
              />
            </div>
          </div>
          <div className="bg-purple-dark text-white p-10 sm:p-12">
            <div className="text-xs tracking-[0.22em] uppercase text-gold-light font-bold mb-4">
              Our director
            </div>
            <div className="h-serif text-2xl text-white mb-1">
              Matthew Brady Renfroe
            </div>
            <div className="text-sm text-white/70 mb-8">Director</div>
            <p className="text-white/90 leading-relaxed h-serif italic text-lg">
              Training the next generation — academic excellence, biblical
              truth, and godly character.
            </p>
            <div className="mt-6 h-px w-12 bg-gold" />
            <p className="text-gold-light/90 leading-relaxed h-serif italic text-base mt-6">
              "This is the Lord's doing; it is marvelous in our eyes."
              <span className="not-italic font-sans text-white/70 text-sm block mt-2 tracking-widest uppercase">
                Psalm 118:23
              </span>
            </p>
            <Link
              to="/enroll"
              data-testid="contact-enroll-cta"
              className="mt-10 inline-flex items-center gap-2 bg-gold text-ink hover:bg-gold-light font-bold px-8 py-4 rounded-sm transition-colors"
            >
              Start Enrollment
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

/* ------------------------- helpers ------------------------- */
function TrustBadge({ Icon, label }) {
  return (
    <div className="flex items-center gap-3 text-white/90">
      <Icon size={20} className="text-gold-light" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

function Step({ n, title, body, divider }) {
  return (
    <div
      className={`p-8 sm:p-10 ${divider ? "md:border-l md:border-grey" : ""}`}
      data-testid={`step-${n}`}
    >
      <div className="h-serif text-gold text-4xl mb-4">{n}</div>
      <h3 className="h-serif text-purple-dark text-xl sm:text-2xl mb-3">{title}</h3>
      <p className="text-ink/80 text-sm leading-relaxed">{body}</p>
    </div>
  );
}

function Value({ Icon, title, body }) {
  return (
    <div>
      <Icon className="text-gold-light mb-4" size={28} />
      <h3 className="h-serif text-white text-xl mb-3">{title}</h3>
      <p className="text-white/75 text-sm leading-relaxed">{body}</p>
    </div>
  );
}

function VerseBlock({ overline, verse, reference, statement }) {
  return (
    <div>
      <div className="text-xs tracking-[0.22em] uppercase text-gold font-bold mb-4">
        {overline}
      </div>
      <blockquote className="border-l-2 border-gold pl-6 mb-6">
        <p className="h-serif italic text-purple-dark text-xl sm:text-2xl leading-snug mb-3">
          &ldquo;{verse}&rdquo;
        </p>
        <cite className="not-italic text-xs tracking-[0.2em] uppercase text-purple-light font-bold">
          {reference}
        </cite>
      </blockquote>
      <p className="text-ink/85 text-base leading-relaxed">{statement}</p>
    </div>
  );
}

function ContactRow({ Icon, label, value, href }) {
  const inner = (
    <div className="flex items-start gap-4 border-b border-grey py-4">
      <Icon size={18} className="text-purple mt-0.5" />
      <div>
        <div className="text-[10px] tracking-[0.2em] uppercase text-purple-light font-bold mb-1">
          {label}
        </div>
        <div className="text-ink text-base">{value}</div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} className="block hover:bg-white transition-colors -mx-2 px-2">
      {inner}
    </a>
  ) : (
    <div>{inner}</div>
  );
}

const FAQ = [
  {
    q: "Is Zion Christian Academy legally recognized in Alabama?",
    a: "Yes. We operate under Alabama's church school statute (Ala. Code § 16-28-1), which recognizes church schools as an exempt category of Alabama education. Upon enrollment, we provide the official documents you'll use to withdraw from public school and notify your county Board of Education.",
  },
  {
    q: "What curriculum does the school use?",
    a: "Zion Christian Academy uses a biblically-grounded curriculum selected by our teacher, Shannon Renfroe, and reviewed by the director. Every subject is taught from a Christ-centered perspective, and students gather for chapel every Wednesday at 1:00 PM.",
  },
  {
    q: "How does tuition work?",
    a: "Tuition is $1,500 per child for the school year. A partner ministry is helping offset costs for this first year, and we offer flexible payment: cash or check at submission, or a payment plan billed toward the end of the school year for families who need it.",
  },
  {
    q: "What documents do we receive after enrolling?",
    a: "Once your application is approved by our director AND payment is received, you'll receive three PDFs: (1) your official Church School Enrollment Form, (2) a Welcome Letter, and (3) BOE Instructions for withdrawing from public school. All are re-downloadable from your parent portal any time.",
  },
  {
    q: "Do you provide report cards, transcripts, and diplomas?",
    a: "Yes. Report cards and enrollment verification letters are available at any time. Transcripts are available on request. High-school students graduate with an official diploma.",
  },
  {
    q: "Who is behind Zion Christian Academy?",
    a: "Zion Christian Academy is an independent Christian community school led by Director Matthew Brady Renfroe and primary teacher Shannon Renfroe. For the 2026–2027 school year, classes meet in-person at Alta Independent Baptist Church fellowship hall (131 Willie Williams Rd, Berry, AL 35546) Monday through Thursday, 8:00 AM to 2:00 PM.",
  },
  {
    q: "When does the school year start?",
    a: "The school year runs August 10, 2026 – May 20, 2027 (with an Aug 1 cutoff for grade placement). Enrollment for the 2026–2027 year is open now.",
  },
];
